import type { ResolvedThemeModeConfiguration } from '@/types/theme_configuration';
import { create_theme_radius_css_variables } from '@/uptk/core/theme/theme_shape';
import { resolve_ink_on_color } from '@/uptk/core/theme/theme_contrast';

type RgbColor = {
  r: number;
  g: number;
  b: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const normalize_channel = (value: number, max: number) => clamp(value, 0, max);

const normalize_hue = (value: number) => {
  const bounded = value % 360;
  return bounded < 0 ? bounded + 360 : bounded;
};

const round_channel = (value: number) => Math.round(value * 10) / 10;

const component_hsl_expression = /^\s*(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%\s*$/i;
const hsl_expression =
  /^\s*hsla?\(\s*(-?\d+(?:\.\d+)?)\s*(?:deg)?(?:\s*,\s*|\s+)(\d+(?:\.\d+)?)%\s*(?:,\s*|\s+)(\d+(?:\.\d+)?)%(?:\s*(?:,\s*|\/\s*)(\d+(?:\.\d+)?%?))?\s*\)\s*$/i;
const rgb_expression =
  /^\s*rgba?\(\s*(\d+(?:\.\d+)?)\s*(?:,\s*|\s+)(\d+(?:\.\d+)?)\s*(?:,\s*|\s+)(\d+(?:\.\d+)?)(?:\s*(?:,\s*|\/\s*)(\d+(?:\.\d+)?%?))?\s*\)\s*$/i;
const hex_expression = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const parse_hex_color = (value: string): RgbColor | null => {
  const match = value.trim().match(hex_expression);
  if (!match) {
    return null;
  }

  const raw = match[1];
  const normalized = raw.length === 3
    ? raw
        .split('')
        .map((character) => `${character}${character}`)
        .join('')
    : raw;

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const parse_rgb_color = (value: string): RgbColor | null => {
  const match = value.trim().match(rgb_expression);
  if (!match) {
    return null;
  }

  return {
    r: normalize_channel(Number(match[1]), 255),
    g: normalize_channel(Number(match[2]), 255),
    b: normalize_channel(Number(match[3]), 255),
  };
};

const hsl_to_rgb = (h: number, s: number, l: number): RgbColor => {
  const saturation = clamp(s / 100, 0, 1);
  const lightness = clamp(l / 100, 0, 1);
  const hue = normalize_hue(h) / 360;

  if (saturation === 0) {
    const channel = Math.round(lightness * 255);
    return { r: channel, g: channel, b: channel };
  }

  const hue_to_rgb = (p: number, q: number, t: number) => {
    let adjustedT = t;
    if (adjustedT < 0) adjustedT += 1;
    if (adjustedT > 1) adjustedT -= 1;
    if (adjustedT < 1 / 6) return p + (q - p) * 6 * adjustedT;
    if (adjustedT < 1 / 2) return q;
    if (adjustedT < 2 / 3) return p + (q - p) * (2 / 3 - adjustedT) * 6;
    return p;
  };

  const q = lightness < 0.5
    ? lightness * (1 + saturation)
    : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  return {
    r: Math.round(hue_to_rgb(p, q, hue + 1 / 3) * 255),
    g: Math.round(hue_to_rgb(p, q, hue) * 255),
    b: Math.round(hue_to_rgb(p, q, hue - 1 / 3) * 255),
  };
};

const parse_hsl_color = (value: string): RgbColor | null => {
  const hslMatch = value.trim().match(hsl_expression);
  if (hslMatch) {
    return hsl_to_rgb(Number(hslMatch[1]), Number(hslMatch[2]), Number(hslMatch[3]));
  }

  const componentMatch = value.trim().match(component_hsl_expression);
  if (!componentMatch) {
    return null;
  }

  return hsl_to_rgb(
    Number(componentMatch[1]),
    Number(componentMatch[2]),
    Number(componentMatch[3])
  );
};

const rgb_to_hsl = ({ r, g, b }: RgbColor) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return {
      h: 0,
      s: 0,
      l: lightness * 100,
    };
  }

  const difference = max - min;
  const saturation =
    lightness > 0.5
      ? difference / (2 - max - min)
      : difference / (max + min);

  let hue = 0;
  switch (max) {
    case red:
      hue = (green - blue) / difference + (green < blue ? 6 : 0);
      break;
    case green:
      hue = (blue - red) / difference + 2;
      break;
    default:
      hue = (red - green) / difference + 4;
      break;
  }

  return {
    h: normalize_hue(hue * 60),
    s: saturation * 100,
    l: lightness * 100,
  };
};

const rgb_to_hex = ({ r, g, b }: RgbColor) => {
  const to_hex = (channel: number) => channel.toString(16).padStart(2, '0');
  return `#${to_hex(r)}${to_hex(g)}${to_hex(b)}`;
};

const parse_css_color = (value: string): RgbColor | null => {
  return parse_hex_color(value) ?? parse_rgb_color(value) ?? parse_hsl_color(value);
};

export const css_color_to_hsl_channels = (value: string | undefined, fallback: string) => {
  if (!value) {
    return fallback;
  }
  const parsed = parse_css_color(value);
  if (!parsed) {
    return fallback;
  }

  const converted = rgb_to_hsl(parsed);
  return `${round_channel(converted.h)} ${round_channel(converted.s)}% ${round_channel(converted.l)}%`;
};

export const css_color_to_rgb_channels = (value: string | undefined, fallback: string) => {
  if (!value) {
    return fallback;
  }
  const parsed = parse_css_color(value);
  if (!parsed) {
    return fallback;
  }

  return `${Math.round(parsed.r)} ${Math.round(parsed.g)} ${Math.round(parsed.b)}`;
};

export const css_color_to_hex = (value: string | undefined, fallback: string) => {
  if (!value) {
    return fallback;
  }
  const parsed = parse_css_color(value);
  if (!parsed) {
    return fallback;
  }

  return rgb_to_hex(parsed);
};

const format_font_family_css_value = (value: string | undefined, fallback: string) => {
  if (!value) {
    return fallback;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }
  if (trimmed.includes(',') || trimmed.startsWith('"') || trimmed.startsWith("'")) {
    return trimmed;
  }
  return trimmed.includes(' ') ? `"${trimmed}"` : trimmed;
};

const mix_channel = (a: number, b: number, t: number): number =>
  Math.round(Math.min(Math.max(a + (b - a) * t, 0), 255));

const mix_hex_for_scale = (base: string, target: string, ratio: number): string => {
  const b = parse_hex_color(base);
  const t = parse_hex_color(target);
  if (!b || !t) return base;
  const r = mix_channel(b.r, t.r, ratio);
  const g = mix_channel(b.g, t.g, ratio);
  const bv = mix_channel(b.b, t.b, ratio);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bv.toString(16).padStart(2, '0')}`.toUpperCase();
};

const build_palette_scale_12 = (base: string): string[] => {
  const hex = css_color_to_hex(base, '#000000');
  return [
    mix_hex_for_scale(hex, '#000000', 0.92),
    mix_hex_for_scale(hex, '#000000', 0.8),
    mix_hex_for_scale(hex, '#000000', 0.68),
    mix_hex_for_scale(hex, '#000000', 0.56),
    mix_hex_for_scale(hex, '#000000', 0.4),
    mix_hex_for_scale(hex, '#000000', 0.2),
    hex.toUpperCase(),
    mix_hex_for_scale(hex, '#FFFFFF', 0.2),
    mix_hex_for_scale(hex, '#FFFFFF', 0.36),
    mix_hex_for_scale(hex, '#FFFFFF', 0.52),
    mix_hex_for_scale(hex, '#FFFFFF', 0.68),
    mix_hex_for_scale(hex, '#FFFFFF', 0.84),
  ];
};

const scale_to_css_entries = (role: string, shades: string[]): Record<string, string> =>
  Object.fromEntries(shades.map((color, i) => [`--color-${role}-${i}`, color]));

const scale_to_hsl_entries = (role: string, shades: string[]): Record<string, string> =>
  Object.fromEntries(
    shades.map((color, i) => [
      `--${role}-${i}`,
      css_color_to_hsl_channels(color, role === 'neutral' ? '210 10% 50%' : '200 60% 50%'),
    ])
  );

/**
 * UPT-1968 — le triptyque `solid / subtle / text` d'une couleur d'état.
 *
 * Le design system attend ces trois variables pour chaque famille ; il les
 * tenait en hexadécimal figé, hors du thème du tenant. On les recalcule ici à
 * partir du jeton, avec deux mélanges qui n'ont pas besoin de connaître le
 * mode : le pastel se pose sur la surface de la page, et le texte se
 * rapproche de l'encre de la page — il fonce donc en clair et s'éclaircit en
 * sombre.
 */
const status_surface_variables = (
  family: 'info' | 'success' | 'warning' | 'danger',
  token: string | undefined,
  fallback: string
): Record<string, string> => {
  const color = css_color_to_hex(token, fallback);
  return {
    [`--c-${family}-solid`]: color,
    [`--c-${family}-subtle`]: `color-mix(in oklab, ${color} 14%, hsl(var(--color-card)))`,
    [`--c-${family}-text`]: `color-mix(in oklab, ${color} 72%, hsl(var(--color-foreground)))`,
  };
};

/**
 * 23.10.2026 (Olivier) — fond de la scène Géode du Studio. Le tenant règle la couleur de base ; la seconde couleur du dégradé
 * s'en déduit (plus claire en sombre, plus foncée en clair). L'encre de la scène reste celle du mode : une couleur qui ne
 * tombe pas du même côté que le fond du portail (clair contre clair, sombre contre sombre) serait illisible et est ignorée.
 */
export const studio_background_css_variables = (studio: string | undefined, background: string | undefined): Record<string, string> => {
  if (!studio) return {};
  const channels = css_color_to_hsl_channels(studio, '');
  const portal = css_color_to_hsl_channels(background, '');
  const lightness = (value: string) => Number.parseFloat(value.split(' ')[2] ?? '');
  const own = lightness(channels), ground = lightness(portal);
  if (!channels || !Number.isFinite(own) || (Number.isFinite(ground) && own < 50 !== ground < 50)) return {};
  const [hue, saturation] = channels.split(' ');
  const end = Math.min(100, Math.max(0, own < 50 ? own + 4 : own - 4));
  return { '--geode-bg-0': channels, '--geode-bg-1': `${hue} ${saturation} ${Math.round(end * 10) / 10}%` };
};

export const create_theme_css_variables = (
  resolved: ResolvedThemeModeConfiguration
): Record<string, string> => {
  const radiusVariables = create_theme_radius_css_variables(resolved.shape.cornerRadius);

  // UPT-1955 : l'encre posee sur une couleur semantique est DERIVEE de la
  // luminance de cette couleur (noir ou blanc, au meilleur contraste). La
  // valeur `primaryForeground` du theme reste prioritaire tant qu'elle est
  // lisible ; sous 3:1 elle est corrigee, sinon un accent clair (Demo : accent
  // vif + texte blanc, 1,88:1) rendait le bouton illisible.
  const explicitInk = resolved.tokens.primaryForeground
    ? css_color_to_hex(resolved.tokens.primaryForeground, '#ffffff')
    : null;
  const inkOn = (color: string | undefined, fallback: string) =>
    resolve_ink_on_color(css_color_to_hex(color, fallback), explicitInk).color;
  const primaryInk = inkOn(resolved.tokens.primary, '#2563eb');
  const accentInk = inkOn(resolved.tokens.accent, '#0ea5e9');
  const successInk = inkOn(resolved.tokens.success, '#16a34a');
  const warningInk = inkOn(resolved.tokens.warning, '#d97706');
  const dangerInk = inkOn(resolved.tokens.danger, '#dc2626');
  const infoInk = inkOn(resolved.tokens.info, '#0284c7');
  // `--bg-inverted` vaut `foreground` : le texte pose dessus doit contraster
  // avec le foreground, pas avec le primaire (en mode sombre, un foreground
  // clair + une encre blanche donnaient blanc sur blanc).
  const invertedInk = inkOn(resolved.tokens.foreground, '#0f172a');
  const bodyFontFamily = format_font_family_css_value(resolved.typography.bodyFontFamily, '"Inter"');
  const displayFontFamily = format_font_family_css_value(
    resolved.typography.displayFontFamily,
    '"Lexend"'
  );
  const labelFontFamily = format_font_family_css_value(
    resolved.typography.labelFontFamily,
    bodyFontFamily
  );

  return ({
    '--color-background': css_color_to_hsl_channels(resolved.tokens.background, '210 40% 98%'),
    '--color-foreground': css_color_to_hsl_channels(resolved.tokens.foreground, '222 47% 11%'),
    '--color-primary': css_color_to_hsl_channels(resolved.tokens.primary, '221 83% 53%'),
    '--color-primary-foreground': css_color_to_hsl_channels(primaryInk, '0 0% 100%'),
    '--color-secondary': css_color_to_hsl_channels(resolved.tokens.surfaceMuted, '214 32% 91%'),
    '--color-secondary-foreground': css_color_to_hsl_channels(
      resolved.tokens.foreground,
      '222 47% 11%'
    ),
    '--color-muted': css_color_to_hsl_channels(resolved.tokens.surfaceMuted, '214 32% 91%'),
    '--color-muted-foreground': css_color_to_hsl_channels(
      resolved.tokens.foregroundMuted,
      '215 19% 35%'
    ),
    '--color-accent': css_color_to_hsl_channels(resolved.tokens.accent, '199 89% 48%'),
    '--color-accent-foreground': css_color_to_hsl_channels(accentInk, '0 0% 100%'),
    '--color-brand': css_color_to_hsl_channels(resolved.tokens.accent, '199 89% 48%'),
    '--color-hover': css_color_to_hsl_channels(resolved.tokens.primary, '217 91% 60%'),
    '--color-hover-background': css_color_to_hsl_channels(
      resolved.tokens.surfaceMuted,
      '214 100% 97%'
    ),
    '--color-destructive': css_color_to_hsl_channels(resolved.tokens.danger, '0 72% 51%'),
    '--color-destructive-foreground': css_color_to_hsl_channels(dangerInk, '0 0% 100%'),
    '--color-card': css_color_to_hsl_channels(resolved.tokens.surface, '0 0% 100%'),
    '--color-card-foreground': css_color_to_hsl_channels(resolved.tokens.foreground, '222 47% 11%'),
    '--color-popover': css_color_to_hsl_channels(resolved.tokens.surface, '0 0% 100%'),
    '--color-popover-foreground': css_color_to_hsl_channels(
      resolved.tokens.foreground,
      '222 47% 11%'
    ),
    '--color-border': css_color_to_hsl_channels(resolved.tokens.border, '214 32% 85%'),
    '--color-input': css_color_to_hsl_channels(resolved.tokens.border, '214 32% 85%'),
    '--color-ring': css_color_to_hsl_channels(resolved.tokens.primary, '221 83% 53%'),
    // UPT-1733 (a): les couleurs d'etat suivent le theme — semantic.css fait
    // pointer --portal-danger/warning/success sur ces --color-*, il suffit
    // donc d'ecrire la source ; ink-on-accent suit le foreground du primaire.
    '--color-success': css_color_to_hsl_channels(resolved.tokens.success, '158 64% 42%'),
    '--color-warning': css_color_to_hsl_channels(resolved.tokens.warning, '42 92% 56%'),
    '--color-danger': css_color_to_hsl_channels(resolved.tokens.danger, '9 78% 58%'),
    '--color-success-foreground': css_color_to_hsl_channels(successInk, '0 0% 100%'),
    '--color-warning-foreground': css_color_to_hsl_channels(warningInk, '0 0% 0%'),
    '--color-danger-foreground': css_color_to_hsl_channels(dangerInk, '0 0% 100%'),
    // UPT-1968 : l'information a sa propre couleur. Elle n'en avait pas et
    // retombait sur celle de la marque : chez un tenant au rouge, une
    // information ressemblait trait pour trait a une erreur.
    '--color-info': css_color_to_hsl_channels(resolved.tokens.info, '200 98% 39%'),
    '--color-info-foreground': css_color_to_hsl_channels(infoInk, '0 0% 100%'),
    // UPT-1968 : les tons pastel du design system suivent enfin le thème.
    //
    // Le design system tenait ses propres `--c-*-subtle/solid/text`, en
    // hexadécimal figé : bandeaux et badges gardaient les couleurs d'usine
    // quel que soit le thème du tenant, et l'information y était bleue alors
    // que le reste de l'écran ne l'était pas. On les réécrit depuis les
    // jetons d'état.
    //
    // Les deux mélanges valent pour les deux modes sans être dupliqués : le
    // pastel se pose sur la surface de la page, et le texte se rapproche de
    // son encre — donc il fonce en clair et s'éclaircit en sombre, toujours
    // du bon côté.
    ...status_surface_variables('info', resolved.tokens.info, '#0284c7'),
    ...status_surface_variables('success', resolved.tokens.success, '#16a34a'),
    ...status_surface_variables('warning', resolved.tokens.warning, '#d97706'),
    ...status_surface_variables('danger', resolved.tokens.danger, '#dc2626'),
    // `--portal-accent` vaut `primary` : l'encre sur accent suit donc le primaire.
    '--portal-ink-on-accent': css_color_to_hsl_channels(primaryInk, '0 0% 100%'),
    '--portal-bg': css_color_to_hsl_channels(resolved.tokens.background, '210 40% 98%'),
    '--portal-surface': css_color_to_hsl_channels(resolved.tokens.surface, '0 0% 100%'),
    '--portal-surface-muted': css_color_to_hsl_channels(resolved.tokens.surfaceMuted, '214 32% 91%'),
    '--ui-surface-1': css_color_to_hsl_channels(resolved.tokens.surface, '0 0% 100%'),
    '--ui-surface-2': css_color_to_hsl_channels(resolved.tokens.surfaceMuted, '214 32% 91%'),
    '--ui-surface-3': css_color_to_hsl_channels(resolved.tokens.background, '210 40% 98%'),
    '--portal-ink': css_color_to_hsl_channels(resolved.tokens.foreground, '222 47% 11%'),
    '--portal-ink-muted': css_color_to_hsl_channels(
      resolved.tokens.foregroundMuted,
      '215 19% 35%'
    ),
    '--portal-border': css_color_to_hsl_channels(resolved.tokens.border, '214 32% 85%'),
    '--portal-accent': css_color_to_hsl_channels(resolved.tokens.primary, '221 83% 53%'),
    '--portal-accent-2': css_color_to_hsl_channels(resolved.tokens.accent, '199 89% 48%'),
    '--portal-accent-soft': css_color_to_hsl_channels(
      resolved.tokens.surfaceMuted,
      '214 100% 97%'
    ),
    '--portal-hero-glow': css_color_to_rgb_channels(resolved.tokens.primary, '59 130 246'),
    '--portal-chart-1': css_color_to_hsl_channels(resolved.tokens.primary, '221 83% 53%'),
    '--portal-chart-2': css_color_to_hsl_channels(resolved.tokens.accent, '199 89% 48%'),
    '--portal-chart-3': css_color_to_hsl_channels(resolved.tokens.success, '158 64% 42%'),
    '--portal-chart-4': css_color_to_hsl_channels(resolved.tokens.warning, '38 92% 50%'),
    '--portal-chart-5': css_color_to_hsl_channels(resolved.tokens.danger, '8 83% 62%'),
    '--portal-chart-6': css_color_to_hsl_channels(resolved.tokens.foregroundMuted, '340 75% 55%'),
    '--portal-chart-7': css_color_to_hsl_channels(resolved.tokens.color7, '215 19% 35%'),
    '--portal-chart-8': css_color_to_hsl_channels(resolved.tokens.color8, '214 32% 85%'),
    '--portal-chart-9': css_color_to_hsl_channels(resolved.tokens.color9, '158 64% 42%'),
    '--portal-chart-10': css_color_to_hsl_channels(resolved.tokens.color10, '0 72% 51%'),
    '--bg-base': css_color_to_hsl_channels(resolved.tokens.background, '210 40% 98%'),
    '--bg-surface': css_color_to_hsl_channels(resolved.tokens.surface, '0 0% 100%'),
    '--bg-surface-muted': css_color_to_hsl_channels(resolved.tokens.surfaceMuted, '214 32% 91%'),
    '--bg-panel': css_color_to_hsl_channels(resolved.tokens.surfaceMuted, '214 32% 91%'),
    '--bg-inverted': css_color_to_hsl_channels(resolved.tokens.foreground, '222 47% 11%'),
    '--border-base': css_color_to_hsl_channels(resolved.tokens.border, '214 32% 85%'),
    '--text-headline': css_color_to_hsl_channels(resolved.tokens.foreground, '222 47% 11%'),
    '--text-body': css_color_to_hsl_channels(resolved.tokens.foregroundMuted, '215 19% 35%'),
    '--text-label': css_color_to_hsl_channels(resolved.tokens.foregroundMuted, '215 19% 35%'),
    '--text-inverted': css_color_to_hsl_channels(invertedInk, '0 0% 100%'),
    '--palette-primary-base': css_color_to_hex(resolved.tokens.primary, '#2563eb'),
    '--palette-secondary-base': css_color_to_hex(resolved.tokens.secondary, '#64748b'),
    '--palette-tertiary-base': css_color_to_hex(resolved.tokens.tertiary, '#0ea5e9'),
    '--palette-neutral-base': css_color_to_hex(resolved.tokens.neutral, '#94a3b8'),
    '--color-1': css_color_to_hex(resolved.tokens.color1, '#2563eb'),
    '--color-2': css_color_to_hex(resolved.tokens.color2, '#0f172a'),
    '--color-3': css_color_to_hex(resolved.tokens.color3, '#0ea5e9'),
    '--color-4': css_color_to_hex(resolved.tokens.color4, '#f8fafc'),
    '--color-5': css_color_to_hex(resolved.tokens.color5, '#ffffff'),
    '--color-6': css_color_to_hex(resolved.tokens.color6, '#e2e8f0'),
    '--color-7': css_color_to_hex(resolved.tokens.color7, '#475569'),
    '--color-8': css_color_to_hex(resolved.tokens.color8, '#cbd5e1'),
    '--color-9': css_color_to_hex(resolved.tokens.color9, '#16a34a'),
    '--color-10': css_color_to_hex(resolved.tokens.color10, '#dc2626'),
    ...studio_background_css_variables(resolved.tokens.studioBackground, resolved.tokens.background),
    '--font-body': bodyFontFamily,
    '--font-headline': displayFontFamily,
    '--font-display': displayFontFamily,
    '--font-label': labelFontFamily,
    '--font-inter': bodyFontFamily,
    '--font-lexend': displayFontFamily,
    ...radiusVariables,
    ...scale_to_hsl_entries('primary', build_palette_scale_12(resolved.tokens.primary ?? '#000000')),
    ...scale_to_hsl_entries('secondary', build_palette_scale_12(resolved.tokens.secondary ?? '#000000')),
    ...scale_to_hsl_entries('tertiary', build_palette_scale_12(resolved.tokens.tertiary ?? '#000000')),
    ...scale_to_hsl_entries('neutral', build_palette_scale_12(resolved.tokens.neutral ?? '#808080')),
    ...scale_to_css_entries('primary', build_palette_scale_12(resolved.tokens.primary ?? '#000000')),
    ...scale_to_css_entries('secondary', build_palette_scale_12(resolved.tokens.secondary ?? '#000000')),
    ...scale_to_css_entries('tertiary', build_palette_scale_12(resolved.tokens.tertiary ?? '#000000')),
    ...scale_to_css_entries('neutral', build_palette_scale_12(resolved.tokens.neutral ?? '#808080')),
  });
};

export const apply_theme_css_variables = (resolved: ResolvedThemeModeConfiguration) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const variables = create_theme_css_variables(resolved);

  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};
